import { COURSE_DURATIONS } from '@/config/courseDurations';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Calculator, CheckCircle, ArrowRight, GraduationCap, Users, Clock, Target } from 'lucide-react';
import { useLocationPricing } from '@/hooks/useLocationPricing';
import { useLocationPopup } from '@/hooks/useLocationPopup';
import LocationPopup from '@/components/LocationPopup';
import { MapPin } from 'lucide-react';
import { buildCourseSchema, offersFromPricing, TUTORING_PROVIDER } from '@/data/courses';
const FoundationsProgram = () => {
  const { isPopupOpen, closePopup, openPopup } = useLocationPopup();
  const { 
    pricing: statePricing, 
    loading: pricingLoading, 
    selectedState, 
    isCenterSpecificPricing, 
    centerName 
  } = useLocationPricing('foundations');
  const kindergartenLabel = selectedState === 'VIC'
    ? 'Prep'
    : selectedState === 'WA'
      ? 'Pre-primary'
      : 'Kindergarten';
  const earlyYearsLabel = selectedState === 'VIC'
    ? 'Prep to Year 2'
    : selectedState === 'WA'
      ? 'Pre-primary to Year 2'
      : 'Kindergarten to Year 2';
  const earlyYearsShort = selectedState === 'VIC'
    ? 'Prep'
    : selectedState === 'WA'
      ? 'Pre-primary'
      : 'Kindergarten';
  const englishCurriculum = [{
    grade: kindergartenLabel,
    skills: ['Letter recognition and sounds', 'Basic phonics and blending', 'Simple sight words', 'Listening comprehension', 'Story sequencing', 'Rhyming and word patterns']
  }, {
    grade: 'Year 1',
    skills: ['Phonics mastery', 'Reading simple sentences', 'Spelling CVC words', 'Basic punctuation', 'Creative writing beginnings', 'Reading comprehension skills']
  }, {
    grade: 'Year 2',
    skills: ['Fluent reading development', 'Spelling patterns and rules', 'Grammar fundamentals', 'Story writing structure', 'Poetry appreciation', 'Reading different text types']
  }, {
    grade: 'Year 3',
    skills: ['Complex sentence structure', 'Persuasive writing techniques', 'Research and note-taking', 'Advanced comprehension strategies', 'Vocabulary expansion', 'Public speaking basics']
  }, {
    grade: 'Year 4',
    skills: ['Essay writing structure', 'Literary device recognition', 'Critical thinking skills', 'Debate and discussion', 'Creative storytelling', 'Advanced grammar rules']
  }, {
    grade: 'Year 5',
    skills: ['Analytical writing', 'Text analysis techniques', 'Advanced punctuation', 'Presentation skills', 'Research methodologies', 'Poetry composition']
  }, {
    grade: 'Year 6',
    skills: ['Sophisticated writing styles', 'Literary criticism', 'Advanced comprehension', 'Exam preparation techniques', 'Independent learning skills', 'Cross-curricular writing']
  }, {
    grade: 'Year 7',
    skills: ['Reading comprehension of literary texts', 'Paragraphing and essay scaffolds', 'Narrative writing forms', 'Sentence structure and syntax', 'Academic vocabulary building', 'Grammar, tense and punctuation']
  }, {
    grade: 'Year 8',
    skills: ['Reading comprehension of informational texts', 'Persuasive writing structure', 'Morphology and word formation', 'Context-based inference', 'Advanced syntax and editing', 'Extended written responses']
  }];
  const mathsCurriculum = [{
    grade: kindergartenLabel,
    skills: ['Number recognition 1-20', 'Counting and cardinality', 'Basic shapes and patterns', 'Size and measurement concepts', 'Simple addition and subtraction', 'Time concepts (morning, afternoon)']
  }, {
    grade: 'Year 1',
    skills: ['Number facts to 20', 'Place value understanding', 'Basic addition and subtraction', '2D and 3D shapes', 'Money recognition', 'Simple data collection']
  }, {
    grade: 'Year 2',
    skills: ['Number facts to 100', 'Multiplication introduction', 'Division concepts', 'Fractions basics', 'Time and calendar', 'Measurement units']
  }, {
    grade: 'Year 3',
    skills: ['Multiplication tables', 'Division strategies', 'Fraction operations', 'Decimal introduction', 'Area and perimeter', 'Data interpretation']
  }, {
    grade: 'Year 4',
    skills: ['Advanced multiplication', 'Long division', 'Fraction equivalence', 'Decimal operations', 'Angle measurement', 'Problem-solving strategies']
  }, {
    grade: 'Year 5',
    skills: ['Percentage calculations', 'Advanced fractions', 'Geometry properties', 'Volume and capacity', 'Algebraic thinking', 'Statistical analysis']
  }, {
    grade: 'Year 6',
    skills: ['Ratio and proportion', 'Advanced algebra', 'Complex geometry', 'Mathematical reasoning', 'Real-world applications', 'Exam preparation']
  }, {
    grade: 'Year 7',
    skills: ['Algebraic expressions and equations', 'Integers, fractions and decimals', 'Percentages and ratio', 'Angles and shape properties', 'Measurement and units', 'Data representation basics']
  }, {
    grade: 'Year 8',
    skills: ['Linear equations and patterns', 'Advanced ratio and proportion', 'Geometric transformations', 'Area, surface area and volume', 'Summary measures in statistics', 'Introduction to probability']
  }];
  const features = ['Individual attention and personalised learning', 'Progress tracking and regular assessments', 'Fun and engaging learning activities', 'Parent updates and communication', 'Small class sizes (8-10 students)', 'Qualified and experienced teachers'];
  return <div className="min-h-screen bg-white">
      <Helmet>
        <title>Foundations Course (K-2) | Maths & English | Schoolola</title>
        <meta name="description" content="Schoolola's Foundations Course builds strong mathematical and literacy foundations for primary school students from Kindergarten to Year 2. Available in-centre across NSW and Victoria, and online Australia-wide." />
        <meta property="og:title" content="Foundations Course (K-2) | Maths & English | Schoolola" />
        <meta property="og:description" content="Strong Maths and English foundations for Kindergarten to Year 2 students. In-centre across NSW and Victoria, plus online." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(buildCourseSchema('foundations', { workload: COURSE_DURATIONS.foundations.iso, offers: offersFromPricing(statePricing), provider: TUTORING_PROVIDER }))}</script>
      </Helmet>
      <LocationPopup isOpen={isPopupOpen} onClose={closePopup} />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-accent-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-accent text-white px-6 py-3 text-lg rounded-2xl">
              Foundations Programme
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900">
              Foundations English & Maths Programme
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Building strong foundations in English and Mathematics for students from {earlyYearsShort} to Year 6 with personalised attention and proven teaching methods in {selectedState}.
            </p>
            
            {/* Center Selection Prompt */}
            <div className="flex justify-center">
              <Button 
                onClick={openPopup}
                variant="outline"
                className="gap-2"
              >
                <MapPin className="h-4 w-4" />
                {centerName ? `${centerName} Center` : 'Select Your Center'}
              </Button>
            </div>
            
            <div className="space-y-6">
              {/* State-specific Pricing with Animation */}
              <div 
                key={`pricing-${selectedState}-${centerName || 'state'}`}
                className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-500"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-center text-primary">
                    {isCenterSpecificPricing ? `${centerName} Pricing` : `${selectedState} Pricing`}
                  </h3>
                  {isCenterSpecificPricing && (
                    <Badge className="bg-accent/10 text-accent text-xs animate-in scale-in duration-300">
                      Center-specific
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-center text-gray-600 mb-3">{COURSE_DURATIONS.foundations.page}</p>
                <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto text-sm">
                  {pricingLoading ? (
                    <div className="col-span-2 text-center text-gray-500">Loading pricing...</div>
                  ) : (
                    <>
                      {(() => {
                        const prep2Price = statePricing.find(p => p.year_level_min === 0 && p.year_level_max === 2)?.price_per_session || 35;
                        const year36Price = statePricing.find(p => p.year_level_min === 3 && p.year_level_max === 6)?.price_per_session || 39;
                        const year78Price = statePricing.find(p => p.year_level_min === 7 && p.year_level_max === 8)?.price_per_session || 55;
                        const isDiscounted = isCenterSpecificPricing && (prep2Price < 35 || year36Price < 39 || year78Price < 55);
                        
                        return (
                          <>
                            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
                              <div className="font-semibold text-primary">
                                {earlyYearsLabel}
                              </div>
                              <div className="text-gray-600">
                                {isDiscounted && prep2Price < 35 && (
                                  <span className="line-through text-gray-400 mr-2">
                                    $35 AUD
                                  </span>
                                )}
                                <span className={`font-semibold ${isDiscounted ? 'text-accent' : 'text-primary'}`}>
                                  ${prep2Price} AUD
                                </span> per session per subject
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {statePricing.find(p => p.year_level_min === 0 && p.year_level_max === 2)?.total_sessions || '10'} sessions total
                              </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
                              <div className="font-semibold text-primary">Year 3 - Year 6</div>
                              <div className="text-gray-600">
                                {isDiscounted && year36Price < 39 && (
                                  <span className="line-through text-gray-400 mr-2">
                                    $39 AUD
                                  </span>
                                )}
                                <span className={`font-semibold ${isDiscounted ? 'text-accent' : 'text-primary'}`}>
                                  ${year36Price} AUD
                                </span> per session per subject
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {statePricing.find(p => p.year_level_min === 3 && p.year_level_max === 6)?.total_sessions || '10'} sessions total
                              </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
                              <div className="font-semibold text-primary">Year 7 - Year 8</div>
                              <div className="text-gray-600">
                                {isDiscounted && year78Price < 55 && (
                                  <span className="line-through text-gray-400 mr-2">
                                    $55 AUD
                                  </span>
                                )}
                                <span className={`font-semibold ${isDiscounted ? 'text-accent' : 'text-primary'}`}>
                                  ${year78Price} AUD
                                </span> per session per subject
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {statePricing.find(p => p.year_level_min === 7 && p.year_level_max === 8)?.total_sessions || '10'} sessions total
                              </div>
                            </div>
                          </>

                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/book-assessment">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 rounded-2xl">
                    Book Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programme Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                About Our Foundations Programme
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our Foundations programme provides comprehensive support for primary school students in English and Mathematics. We focus on building strong fundamental skills that will serve as the foundation for all future learning.
              </p>
              <ul className="space-y-4">
                {features.map((feature, index) => <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>)}
              </ul>
            </div>
            <div className="relative">
              <img src="/lovable-uploads/foundations-hero-2025-optimized.webp" alt="Students learning foundations English and Maths in Schoolola classroom" className="rounded-2xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Details */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
              What We Teach by Grade Level
            </h2>
            <p className="text-xl text-gray-600">
              Detailed curriculum breakdown for English and Mathematics from {kindergartenLabel} to Year 8
            </p>
          </div>

          <Tabs defaultValue="english" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-12 h-auto">
              <TabsTrigger value="english" className="flex flex-col items-center p-6 space-y-2 data-[state=active]:bg-primary data-[state=active]:text-white">
                <BookOpen className="h-6 w-6" />
                <span className="font-medium">English Curriculum</span>
              </TabsTrigger>
              <TabsTrigger value="maths" className="flex flex-col items-center p-6 space-y-2 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Calculator className="h-6 w-6" />
                <span className="font-medium">Mathematics Curriculum</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="english" className="mt-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {englishCurriculum.map((grade, index) => <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <GraduationCap className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-gray-900">
                          {grade.grade}
                        </h3>
                      </div>
                      <ul className="space-y-2">
                        {grade.skills.map((skill, idx) => <li key={idx} className="flex items-start space-x-2">
                            <div className="h-1.5 w-1.5 bg-accent rounded-full flex-shrink-0 mt-2"></div>
                            <span className="text-sm text-gray-600">{skill}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>)}
              </div>
            </TabsContent>

            <TabsContent value="maths" className="mt-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {mathsCurriculum.map((grade, index) => <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-accent/10 rounded-full flex items-center justify-center">
                          <Calculator className="h-5 w-5 text-accent" />
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-gray-900">
                          {grade.grade}
                        </h3>
                      </div>
                      <ul className="space-y-2">
                        {grade.skills.map((skill, idx) => <li key={idx} className="flex items-start space-x-2">
                            <div className="h-1.5 w-1.5 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                            <span className="text-sm text-gray-600">{skill}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>)}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
              Why Choose Our Foundations Programme?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg text-center">
              <CardContent className="p-8 space-y-4">
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-gray-900">
                  Small Class Sizes
                </h3>
                <p className="text-gray-600">10-12 students per class ensuring personalised attention for every child.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg text-center">
              <CardContent className="p-8 space-y-4">
                <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
                  <Target className="h-6 w-6 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-gray-900">
                  Individualised Learning
                </h3>
                <p className="text-gray-600">
                  Tailored programmes that adapt to each student's learning pace and style.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg text-center">
              <CardContent className="p-8 space-y-4">
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-gray-900">
                  Progress Tracking
                </h3>
                <p className="text-gray-600">
                  Regular assessments and detailed progress reports to keep parents informed.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white">
              Build Strong Foundations for Your Child's Future
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed">
              Give your child the best start in their educational journey with our comprehensive Foundations programme.
            </p>
            <Link to="/book-assessment">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-lg px-8 py-4 rounded-2xl min-h-[52px] font-semibold shadow-xl">
                Book Your Free Assessment Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default FoundationsProgram;